import { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Download, FileText, LogOut, Calendar, Truck, Sparkles, Wallet, CalendarPlus, CalendarDays, ClipboardCheck, Wrench, HeartHandshake } from 'lucide-react';

import StaffAttendanceCard from '../components/staff/StaffAttendanceCard';
import UnpaidLeaveBreakdown from '../components/staff/UnpaidLeaveBreakdown';
import VehicleInspectionReport from '../components/staff/VehicleInspectionReport';
import DailyTripsCard from '../components/staff/DailyTripsCard';
import { PerformanceNoticeCard } from '../components/staff/PerformanceNoticeCard';
import StaffLoanCard from '../components/staff/StaffLoanCard';
import AssistantsPicker from '../components/staff/AssistantsPicker';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const R = (n) => 'R ' + Number(n || 0).toLocaleString('en-ZA', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const MONTH_NAMES = ['', 'January','February','March','April','May','June','July','August','September','October','November','December'];
const fmtMonth = (ym) => { if (!ym) return '-'; const [y,m] = ym.split('-'); return `${MONTH_NAMES[parseInt(m)] || m} ${y}`; };

export default function StaffDashboard() {
  const { user, logout } = useAuth();
  const [slips, setSlips] = useState([]);
  const [loading, setLoading] = useState(true);
  const [balance, setBalance] = useState(null);
  const [leaves, setLeaves] = useState([]);
  const [dlgOpen, setDlgOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const todayISO = () => new Date().toISOString().slice(0, 10);
  const [form, setForm] = useState({
    leave_type: 'annual',
    start_date: todayISO(),
    end_date: todayISO(),
    reason: '',
  });

  const token = localStorage.getItem('token') || sessionStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  const fetchSlips = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/api/internal-staff/payslips`, { headers });
      setSlips(res.data);
    } catch { toast.error('Failed to load payslips'); }
    finally { setLoading(false); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchLeave = useCallback(async () => {
    if (!user?.id) return;
    try {
      const [bal, reqs] = await Promise.all([
        axios.get(`${API_URL}/api/leave/balance/${user.id}`, { headers }),
        axios.get(`${API_URL}/api/leave/requests`, { headers }),
      ]);
      setBalance(bal.data);
      setLeaves(reqs.data || []);
    } catch { /* silent */ }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  useEffect(() => { fetchSlips(); }, [fetchSlips]);
  useEffect(() => { fetchLeave(); }, [fetchLeave]);

  const submitLeave = async () => {
    if (!form.start_date || !form.end_date) return toast.error('Pick a date range');
    if (form.end_date < form.start_date) return toast.error('End date is before start date');
    setSaving(true);
    try {
      await axios.post(`${API_URL}/api/leave/requests`, form, { headers });
      toast.success('Leave request submitted — awaiting approval');
      setDlgOpen(false);
      setForm({ leave_type: 'annual', start_date: todayISO(), end_date: todayISO(), reason: '' });
      await fetchLeave();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Submit failed');
    } finally { setSaving(false); }
  };

  const download = async (s) => {
    try {
      const res = await axios.get(`${API_URL}/api/internal-staff/payslips/${s.id}/pdf`, { headers, responseType: 'blob' });
      const blob = new Blob([res.data], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `JSM_Payslip_${(user?.full_name || 'staff').replace(/\s+/g, '_')}_${s.month}.pdf`;
      a.click(); URL.revokeObjectURL(url);
      toast.success('Payslip downloaded');
    } catch { toast.error('Download failed'); }
  };

  // Aggregate insights
  const ytdTotal = slips.reduce((a, b) => a + (b.net_pay || 0), 0);
  const latest = slips[0];
  const ytdDeliveries = slips.reduce((a, b) => a + (b.deliveries_count || 0), 0);

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 dark:from-slate-900 dark:to-slate-800">
      {/* Top bar */}
      <header className="bg-[#0f4c75] text-white shadow-md">
        <div className="max-w-6xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Truck className="h-7 w-7" />
            <div>
              <h1 className="font-bold text-lg leading-tight">JSM Transport</h1>
              <p className="text-xs opacity-80 leading-tight">Staff Portal</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <span className="text-sm hidden sm:inline">Hi, {user?.full_name}</span>
            <Button variant="ghost" size="icon" onClick={logout} className="text-white hover:bg-white/10" data-testid="staff-logout">
              <LogOut className="h-5 w-5" />
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto p-4 space-y-4">
        {/* Assigned vehicle banner — surfaces the fleet-side assignment for the driver */}
        {user?.assigned_vehicle_registration && (
          <Card className="border-indigo-200 bg-gradient-to-r from-indigo-50 to-sky-50" data-testid="staff-assigned-vehicle">
            <CardContent className="py-3 px-4 flex items-center gap-3">
              <div className="rounded-full bg-indigo-600 text-white p-2">
                <Truck className="h-5 w-5" />
              </div>
              <div className="flex-1">
                <div className="text-[10px] uppercase tracking-wider text-indigo-700 font-semibold">Your Vehicle</div>
                <div className="text-xl font-extrabold text-indigo-900 leading-tight">
                  {user.assigned_vehicle_registration}
                </div>
                {user.assigned_vehicle_name && (
                  <div className="text-xs text-indigo-700/80">{user.assigned_vehicle_name}</div>
                )}
              </div>
              <Badge className="bg-indigo-600 hover:bg-indigo-600 text-white">Assigned</Badge>
            </CardContent>
          </Card>
        )}

        {/* Numeric tiles hidden on login — staff can see their numbers inside the Welfare tab */}

        <PerformanceNoticeCard />

        {/* 3 primary tabs: Attendance / Welfare / Vehicle */}
        <Tabs defaultValue="attendance" className="w-full">
          <TabsList className="grid grid-cols-3 w-full h-auto" data-testid="staff-tabs">
            <TabsTrigger value="attendance" className="gap-2 py-3" data-testid="tab-attendance">
              <ClipboardCheck className="h-4 w-4" /> Staff Attendance
            </TabsTrigger>
            <TabsTrigger value="welfare" className="gap-2 py-3" data-testid="tab-welfare">
              <HeartHandshake className="h-4 w-4" /> Staff Welfare
            </TabsTrigger>
            <TabsTrigger value="vehicle" className="gap-2 py-3" data-testid="tab-vehicle">
              <Wrench className="h-4 w-4" /> Vehicle Inspection & Report
            </TabsTrigger>
          </TabsList>

          {/* ============ ATTENDANCE ============ */}
          <TabsContent value="attendance" className="space-y-4 pt-4">
            <StaffAttendanceCard />
            <AssistantsPicker testIdPrefix="staff" />
            <DailyTripsCard />
          </TabsContent>

          {/* ============ WELFARE (Payslips + Leave + Unpaid Breakdown) ============ */}
          <TabsContent value="welfare" className="space-y-4 pt-4">
            {/* Leave summary + Request button */}
            <Card data-testid="my-leave-card">
              <CardHeader className="flex flex-row items-center justify-between pb-2">
                <CardTitle className="text-base flex items-center gap-2">
                  <CalendarDays className="h-4 w-4 text-indigo-600" />
                  My Leave
                </CardTitle>
                <Button size="sm" onClick={() => setDlgOpen(true)} className="bg-indigo-600 hover:bg-indigo-700" data-testid="request-leave-btn">
                  <CalendarPlus className="h-4 w-4 mr-1" /> Request Leave
                </Button>
              </CardHeader>
              <CardContent>
                {balance ? (
                  <>
                  <div className="grid grid-cols-3 gap-3 text-center mb-3">
                    <div className="rounded-lg bg-slate-50 border p-3">
                      <div className="text-[10px] uppercase text-muted-foreground">Accrued</div>
                      <div className="text-xl font-bold" data-testid="my-accrued">{balance.accrued.toFixed(2)}</div>
                    </div>
                    <div className="rounded-lg bg-slate-50 border p-3">
                      <div className="text-[10px] uppercase text-muted-foreground">Used</div>
                      <div className="text-xl font-bold text-rose-600" data-testid="my-used">{balance.used.toFixed(2)}</div>
                    </div>
                    <div className="rounded-lg bg-emerald-50 border border-emerald-200 p-3">
                      <div className="text-[10px] uppercase text-emerald-700">Remaining</div>
                      <div className="text-xl font-extrabold text-emerald-700" data-testid="my-remaining">{balance.remaining.toFixed(2)}</div>
                    </div>
                  </div>

                  {/* Leave already booked — stacked bar with paid + unpaid split */}
                  {(() => {
                    const entitlement = Math.max(Number(balance.entitlement) || 0, 0);
                    const paidUsed = Number(balance.paid_used) || 0;
                    const unpaidUsed = Number(balance.unpaid_used) || 0;
                    const remain = Math.max(entitlement - paidUsed, 0);
                    const total = entitlement + unpaidUsed || 1;
                    const pct = (n) => Math.max(0, Math.min(100, (n / total) * 100));
                    return (
                      <div className="mb-4" data-testid="leave-booked-bar">
                        <div className="flex items-center justify-between text-[11px] mb-1">
                          <span className="font-semibold text-slate-700">Leave already booked this year</span>
                          <span className="text-muted-foreground">
                            <b className="text-emerald-700">{paidUsed.toFixed(2)}</b> paid
                            {unpaidUsed > 0 && (<> · <b className="text-rose-700">{unpaidUsed.toFixed(2)}</b> unpaid</>)}
                            {' '}of {entitlement.toFixed(2)} accrued
                          </span>
                        </div>
                        <div className="h-3 rounded-full overflow-hidden flex bg-slate-100 border" title={`Paid ${paidUsed} · Unpaid ${unpaidUsed} · Remaining ${remain}`}>
                          {paidUsed > 0 && (
                            <div className="bg-emerald-500 h-full" style={{ width: `${pct(paidUsed)}%` }} data-testid="lbb-paid" />
                          )}
                          {unpaidUsed > 0 && (
                            <div className="bg-rose-500 h-full" style={{ width: `${pct(unpaidUsed)}%` }} data-testid="lbb-unpaid" />
                          )}
                          {remain > 0 && (
                            <div className="bg-emerald-100 h-full" style={{ width: `${pct(remain)}%` }} data-testid="lbb-remaining" />
                          )}
                        </div>
                        <div className="flex gap-3 text-[10px] mt-1 text-muted-foreground">
                          <span className="inline-flex items-center gap-1"><span className="w-2 h-2 bg-emerald-500 rounded-sm inline-block" /> Paid used</span>
                          {unpaidUsed > 0 && (<span className="inline-flex items-center gap-1"><span className="w-2 h-2 bg-rose-500 rounded-sm inline-block" /> Unpaid used</span>)}
                          <span className="inline-flex items-center gap-1"><span className="w-2 h-2 bg-emerald-100 border border-emerald-300 rounded-sm inline-block" /> Remaining</span>
                        </div>
                      </div>
                    );
                  })()}
                  </>
                ) : (
                  <div className="text-center text-sm text-muted-foreground py-3">Loading leave balance…</div>
                )}
                {leaves.length === 0 ? (
                  <p className="text-xs text-muted-foreground text-center">You haven't made any leave requests yet.</p>
                ) : (
                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Type</TableHead>
                          <TableHead>Dates</TableHead>
                          <TableHead className="text-center">Days</TableHead>
                          <TableHead>Status</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {leaves.slice(0, 5).map(l => (
                          <TableRow key={l.id} data-testid={`my-leave-${l.id}`}>
                            <TableCell className="capitalize">{l.leave_type.replace(/_/g, ' ')}</TableCell>
                            <TableCell className="text-sm">{l.start_date} → {l.end_date}</TableCell>
                            <TableCell className="text-center">{l.days}</TableCell>
                            <TableCell>
                              <Badge
                                variant="outline"
                                className={
                                  l.status === 'approved' ? 'bg-emerald-100 text-emerald-800 border-emerald-300'
                                  : l.status === 'declined' ? 'bg-rose-100 text-rose-800 border-rose-300'
                                  : l.status === 'cancelled' ? 'bg-slate-100 text-slate-700'
                                  : 'bg-amber-100 text-amber-800 border-amber-300'
                                }
                              >{l.status}</Badge>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Unpaid Leave Breakdown — powered by /api/leave/unpaid-breakdown */}
            <UnpaidLeaveBreakdown staffId={user?.id} />

            {/* Staff Loan card — up to R1 000 with digital signature */}
            <StaffLoanCard />

            {/* Payslips list */}
            <Card>
              <CardHeader>
                <CardTitle className="text-base flex items-center gap-2">
                  <FileText className="h-5 w-5 text-blue-600" />
                  My Payslips
                </CardTitle>
              </CardHeader>
              <CardContent className="p-0 overflow-x-auto">
                {loading ? (
                  <div className="text-center py-8 text-muted-foreground">Loading...</div>
                ) : slips.length === 0 ? (
                  <div className="text-center py-12 text-muted-foreground">
                    <FileText className="h-10 w-10 mx-auto mb-3 opacity-40" />
                    <p>You don't have any payslips yet.</p>
                    <p className="text-xs mt-1">Once your manager issues one, it'll appear here for download.</p>
                  </div>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Month</TableHead>
                        <TableHead className="text-center">Days</TableHead>
                        <TableHead className="text-center">Deliveries</TableHead>
                        <TableHead className="text-right">Rate</TableHead>
                        <TableHead className="text-right">Basic</TableHead>
                        <TableHead className="text-right">Incentive</TableHead>
                        <TableHead className="text-right">Unpaid Leave</TableHead>
                        <TableHead className="text-right">Deductions</TableHead>
                        <TableHead className="text-right">Net Pay</TableHead>
                        <TableHead className="text-right">PDF</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {slips.map(s => (
                        <TableRow key={s.id} data-testid={`my-slip-${s.month}`}>
                          <TableCell className="font-semibold">
                            <div className="flex items-center gap-1.5"><Calendar className="h-3.5 w-3.5 text-slate-400" />{fmtMonth(s.month)}</div>
                          </TableCell>
                          <TableCell className="text-center">{s.days_worked}</TableCell>
                          <TableCell className="text-center">{s.deliveries_count}</TableCell>
                          <TableCell className="text-right text-muted-foreground">{R(s.daily_rate)}</TableCell>
                          <TableCell className="text-right">{R(s.basic_pay)}</TableCell>
                          <TableCell className="text-right text-emerald-600">
                            {R(s.incentive)}
                            {s.incentive_note && <div className="text-[10px] text-muted-foreground italic">{s.incentive_note}</div>}
                          </TableCell>
                          <TableCell className="text-right text-rose-600">
                            {s.unpaid_leave_days > 0 ? (
                              <>
                                -{R(s.unpaid_leave_amount)}
                                <div className="text-[10px] text-muted-foreground italic">{s.unpaid_leave_days} day(s)</div>
                              </>
                            ) : '—'}
                          </TableCell>
                          <TableCell className="text-right text-rose-600">
                            -{R(s.deductions)}
                            {s.deductions_note && <div className="text-[10px] text-muted-foreground italic">{s.deductions_note}</div>}
                          </TableCell>
                          <TableCell className="text-right font-extrabold text-indigo-600 text-base">{R(s.net_pay)}</TableCell>
                          <TableCell className="text-right">
                            <Button size="sm" onClick={() => download(s)} className="bg-blue-600 hover:bg-blue-700 h-8" data-testid={`download-${s.month}`}>
                              <Download className="h-3.5 w-3.5 mr-1" /> PDF
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* ============ VEHICLE INSPECTION & REPORT ============ */}
          <TabsContent value="vehicle" className="space-y-4 pt-4">
            <VehicleInspectionReport />
          </TabsContent>
        </Tabs>

        <p className="text-center text-xs text-muted-foreground">
          For queries about your payslip, contact JSM Transport on 062 017 4905.
        </p>
      </main>

      {/* Request Leave dialog */}
      <Dialog open={dlgOpen} onOpenChange={setDlgOpen}>
        <DialogContent data-testid="staff-leave-dialog" className="max-w-md">
          <DialogHeader>
            <DialogTitle>Request Leave</DialogTitle>
            <DialogDescription>
              Approved <b>annual</b> leave is deducted from your remaining balance.
              {balance && (
                <> Your remaining: <b className="text-emerald-700">{balance.remaining.toFixed(2)}</b> days.</>
              )}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <label className="text-sm font-medium">Leave Type</label>
              <Select value={form.leave_type} onValueChange={(v) => setForm({ ...form, leave_type: v })}>
                <SelectTrigger data-testid="staff-dlg-type"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="annual">Annual (paid)</SelectItem>
                  <SelectItem value="sick">Sick</SelectItem>
                  <SelectItem value="unpaid">Unpaid</SelectItem>
                  <SelectItem value="family_responsibility">Family Responsibility</SelectItem>
                  <SelectItem value="other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-sm font-medium">Start Date</label>
                <Input type="date" value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} data-testid="staff-dlg-start" />
              </div>
              <div>
                <label className="text-sm font-medium">End Date</label>
                <Input type="date" value={form.end_date} onChange={(e) => setForm({ ...form, end_date: e.target.value })} data-testid="staff-dlg-end" />
              </div>
            </div>
            <div>
              <label className="text-sm font-medium">Reason (optional)</label>
              <Input value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })} data-testid="staff-dlg-reason" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDlgOpen(false)}>Cancel</Button>
            <Button onClick={submitLeave} disabled={saving} className="bg-indigo-600 hover:bg-indigo-700" data-testid="staff-dlg-submit">
              {saving ? 'Submitting…' : 'Submit Request'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
